import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ZoomIn, ZoomOut, RotateCcw, Check, Crop as CropIcon } from "lucide-react";

/**
 * Simple avatar cropper — always a square 1:1 aspect ratio.
 * Returns a JPEG Blob at 512x512.
 */
export default function PhotoCropper({ open, imageSrc, onCancel, onSave, saving = false }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((_, area) => setCroppedAreaPixels(area), []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, rotation);
    onSave(blob);
  };

  const reset = () => { setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-lg" data-testid="photo-cropper">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CropIcon className="h-4 w-4" /> Crop & zoom your photo</DialogTitle>
        </DialogHeader>

        <div className="relative w-full h-80 bg-slate-900 rounded-lg overflow-hidden">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
            />
          )}
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <ZoomOut className="h-4 w-4 text-slate-400" />
            <Slider min={1} max={3} step={0.05} value={[zoom]} onValueChange={([v]) => setZoom(v)} className="flex-1" data-testid="crop-zoom" />
            <ZoomIn className="h-4 w-4 text-slate-400" />
          </div>
          <div className="flex items-center gap-3">
            <RotateCcw className="h-4 w-4 text-slate-400" />
            <Slider min={-180} max={180} step={5} value={[rotation]} onValueChange={([v]) => setRotation(v)} className="flex-1" data-testid="crop-rotate" />
            <span className="text-xs font-mono text-slate-500 w-10 text-right">{rotation}°</span>
          </div>
          <div className="flex justify-between text-[11px] text-slate-500">
            <button type="button" onClick={reset} className="hover:text-slate-900" data-testid="crop-reset">Reset</button>
            <span>Drag the image to reposition · pinch or use slider to zoom</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !croppedAreaPixels} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="crop-save">
            <Check className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save photo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Read as data URL — used by callers to convert File → src for the cropper. */
export function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

// ---- crop helpers ----
function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    img.setAttribute("crossOrigin", "anonymous");
    img.src = url;
  });
}
function getRadianAngle(deg) { return (deg * Math.PI) / 180; }

async function getCroppedBlob(imageSrc, pixelCrop, rotation = 0) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  canvas.width = safeArea;
  canvas.height = safeArea;

  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate(getRadianAngle(rotation));
  ctx.translate(-safeArea / 2, -safeArea / 2);

  ctx.drawImage(image, safeArea / 2 - image.width * 0.5, safeArea / 2 - image.height * 0.5);
  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  const OUT = 512;
  canvas.width = OUT;
  canvas.height = OUT;

  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x) - (pixelCrop.width - OUT) / 2 * 0,
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y) - (pixelCrop.height - OUT) / 2 * 0
  );

  // Simpler & correct: draw from source rect onto 512x512
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = safeArea; tempCanvas.height = safeArea;
  const tctx = tempCanvas.getContext("2d");
  tctx.translate(safeArea / 2, safeArea / 2);
  tctx.rotate(getRadianAngle(rotation));
  tctx.translate(-safeArea / 2, -safeArea / 2);
  tctx.drawImage(image, safeArea / 2 - image.width * 0.5, safeArea / 2 - image.height * 0.5);

  const finalCanvas = document.createElement("canvas");
  finalCanvas.width = OUT; finalCanvas.height = OUT;
  const fctx = finalCanvas.getContext("2d");
  fctx.drawImage(
    tempCanvas,
    Math.round(safeArea / 2 - image.width * 0.5 + pixelCrop.x),
    Math.round(safeArea / 2 - image.height * 0.5 + pixelCrop.y),
    pixelCrop.width, pixelCrop.height,
    0, 0, OUT, OUT
  );

  return new Promise((resolve) => {
    finalCanvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.9);
  });
}
